import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, buttonClass, Card, fieldClass, IconCopy, IconMail, IconMessage, labelClass } from '@timely/ui';
import {
    TEMPLATE_VARIABLES, extractVariables, holds, isTemplateVariable, mailtoLink, reminderRecipients, renderTemplate, whatsappLink, whatsappNumber,
    type Customer, type TemplateContext, type TenantContext,
} from '@timely/domain';
import { useFormatters } from '../book/useFormatters';
import { paths } from '../workspace/navigation';
import { useTemplates, type MessageTemplate } from './useTemplates';

const CHANNEL = { whatsapp: 'WhatsApp', email: 'Email', internal: 'Internal note' } as const;

/**
 * A reminder, from the account (PRODUCT-UX-FOUNDATION §9).
 *
 * The company's templates, filled in for this customer, and opened in the
 * person's own WhatsApp or email — or copied. The product sends nothing and so
 * records nothing: whoever sends it records it in "Record what happened", the
 * same as a call. Internal-note templates are not for customers and are not
 * offered here.
 *
 * It can go to anybody on the account: the contact from the sheet, or one of
 * the other people listed in the product (0033). The message greets whoever
 * it goes to.
 */
export function SendReminder({ context, customer, bare = false }: {
    context: TenantContext;
    customer: Customer;
    /** Inside a section that already has its heading and surface. */
    bare?: boolean;
}) {
    const { workspace } = context;
    const { money, date } = useFormatters();
    const { templates, loading, error } = useTemplates(workspace.id);
    const [chosen, setChosen] = useState('');
    const [to, setTo] = useState('');
    const [copied, setCopied] = useState(false);

    const recipients = reminderRecipients(customer, customer.additionalContacts ?? []);
    const recipient = recipients.find(r => r.key === to) ?? recipients[0];
    const recipientName = recipient?.key === 'primary'
        ? customer.contactPerson
        : recipient ? recipient.label.replace(/ \(.*\)$/, '') : customer.contactPerson;

    const usable = templates
        .filter(t => !t.archivedAt && t.channel !== 'internal')
        .sort(byOffer);
    const template = usable.find(t => t.id === chosen) ?? usable[0];

    const values: TemplateContext = {
        customer_name: customer.company,
        outstanding_amount: money.exact(customer.total),
        company_name: workspace.name,
        ...(customer.followUpDate ? { follow_up_date: date.long(customer.followUpDate) } : {}),
        ...(customer.forecastAmount ? { expected_amount: money.exact(customer.forecastAmount) } : {}),
        // The promised day, while the promise is open (answering one clears it, 0033).
        ...(customer.forecastDate && Number(customer.forecastAmount) > 0 && customer.status !== 'Completed'
            ? { promised_date: date.long(customer.forecastDate) } : {}),
        ...(recipientName ? { contact_name: recipientName } : {}),
    };
    // A placeholder this account cannot fill renders as nothing; say so before it is sent, not after.
    const blank = template
        ? extractVariables(template.content).filter(isTemplateVariable).filter(v => !values[v])
            .map(v => TEMPLATE_VARIABLES.find(d => d.key === v)!.label.toLowerCase())
        : [];
    // Plain text either way: a WhatsApp message and a mailto body are both
    // text, and HTML escaping would put `&amp;` in front of the customer.
    const text = template ? renderTemplate(template.content, values, 'internal') : '';
    const number = whatsappNumber(recipient?.phone ?? '', workspace.phoneCountry);
    const email = recipient?.email ?? '';

    const copy = () => {
        void navigator.clipboard?.writeText(text).then(
            () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
            () => setCopied(false),
        );
    };

    const content = (
        <>
            {loading ? (
                <p className="text-secondary text-label-3">Loading your templates…</p>
            ) : error ? (
                <p className="text-secondary text-label-3">{error}</p>
            ) : !template ? (
                <p className="text-secondary text-label-3">
                    There are no message templates yet.{' '}
                    {holds(context, 'templates.manage') ? (
                        <Link to={paths(workspace.slug).setting('templates')} className="text-accent-text font-semibold hover:underline">
                            Write one
                        </Link>
                    ) : 'Ask a manager to write one.'}
                </p>
            ) : (
                <>
                    {recipients.length > 1 && (
                        <div className="mb-3">
                            <label htmlFor="reminder-to" className={labelClass}>Send to</label>
                            <select
                                id="reminder-to"
                                value={recipient?.key ?? ''}
                                onChange={e => { setTo(e.target.value); setCopied(false); }}
                                className={fieldClass({ className: 'w-full' })}
                            >
                                {recipients.map(r => (
                                    <option key={r.key} value={r.key}>{r.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {usable.length > 1 && (
                        <div className="mb-3">
                            <label htmlFor="reminder-template" className={labelClass}>Template</label>
                            <select
                                id="reminder-template"
                                value={template.id}
                                onChange={e => { setChosen(e.target.value); setCopied(false); }}
                                className={fieldClass({ className: 'w-full' })}
                            >
                                {usable.map(t => (
                                    <option key={t.id} value={t.id}>{t.name} · {CHANNEL[t.channel]}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="text-caption font-semibold text-label-3 mb-1">
                        {usable.length > 1 ? 'Message' : `${template.name} · ${CHANNEL[template.channel]}`}
                    </div>
                    {blank.length > 0 && (
                        <p role="note" className="mb-2 rounded-control bg-warn-bg px-3 py-2 text-secondary text-label-2">
                            This account has no {blank.join(' or ')}, so {blank.length === 1 ? 'it is' : 'they are'} left blank below.
                            Choose another template, or check the message before you send it.
                        </p>
                    )}
                    {/* A long message scrolls inside its box, so the box takes focus: a keyboard must reach all of it. */}
                    <div
                        role="region"
                        tabIndex={0}
                        aria-label="The message, filled in for this customer"
                        className="text-secondary text-label-2 whitespace-pre-wrap break-words bg-card-2 rounded-control px-3 py-2 max-h-48 overflow-auto focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                    >
                        {text}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {template.channel === 'whatsapp' && number && (
                            <a
                                href={whatsappLink(number, text)}
                                target="_blank" rel="noopener noreferrer"
                                className={buttonClass({ variant: 'primary', size: 'sm' })}
                            >
                                <IconMessage size={14} />Open in WhatsApp
                            </a>
                        )}
                        {template.channel === 'email' && email && (
                            <a
                                href={mailtoLink(email, `${workspace.name}: payment reminder`, text)}
                                className={buttonClass({ variant: 'primary', size: 'sm' })}
                            >
                                <IconMail size={14} />Open in email
                            </a>
                        )}
                        <Button size="sm" variant="secondary" onClick={copy} icon={<IconCopy size={14} />}>
                            {copied ? 'Copied' : 'Copy message'}
                        </Button>
                    </div>
                    {template.channel === 'whatsapp' && !number && (
                        <p className="text-caption text-label-3 mt-2">{recipients.length > 1 ? 'This person has' : 'This account has'} no usable phone number, so the message can only be copied.</p>
                    )}
                    {template.channel === 'email' && !email && (
                        <p className="text-caption text-label-3 mt-2">{recipients.length > 1 ? 'This person has' : 'This account has'} no email address, so the message can only be copied.</p>
                    )}
                    <p className="text-caption text-label-3 mt-2" role="status">
                        {copied ? 'Copied to the clipboard.' : 'Nothing is sent from here. Once it has gone, record it under Record what happened.'}
                    </p>
                </>
            )}
        </>
    );

    return bare ? content : (
        <Card className="px-5 py-4">
            <h3 className="text-body font-semibold text-label mb-2">Send a reminder</h3>
            {content}
        </Card>
    );
}

/** The company's default for a channel first, WhatsApp before email, then by name. */
const byOffer = (a: MessageTemplate, b: MessageTemplate): number =>
    Number(b.isDefault) - Number(a.isDefault)
    || (a.channel === b.channel ? 0 : a.channel === 'whatsapp' ? -1 : 1)
    || a.name.localeCompare(b.name);
